import React, { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { formatBRL } from "@/lib/utils";
import { PAYMENT_METHOD_LABELS } from "@/features/finance/components/FinanceStatusBadge";

/**
 * Register a manual payment against a specific installment.
 * Props:
 *  - open, onOpenChange
 *  - installment: { id, number, amount, paid_amount, due_date }
 *  - accountLabel: string (e.g., "AR-00001")
 *  - onSubmit: async ({ installment_id, amount, method, paid_at, notes }) => void
 */
export function RegisterPaymentDialog({
    open,
    onOpenChange,
    installment,
    accountLabel,
    onSubmit,
    submitting,
}) {
    const balance = installment
        ? Number(installment.amount || 0) - Number(installment.paid_amount || 0)
        : 0;
    const [amount, setAmount] = useState("");
    const [method, setMethod] = useState("pix");
    const [paidAt, setPaidAt] = useState(() =>
        new Date().toISOString().slice(0, 10),
    );
    const [notes, setNotes] = useState("");
    const [error, setError] = useState("");

    // Reset form whenever the dialog is (re)opened on a specific installment.
    // We intentionally recompute the fresh balance inside the effect so the
    // dep array can stay minimal (open + installment identity + amount/paid).
    const installmentId = installment?.id;
    const installmentAmount = installment?.amount;
    const installmentPaid = installment?.paid_amount;
    React.useEffect(() => {
        if (!open || !installmentId) return;
        const nextBalance = Number(installmentAmount || 0) - Number(installmentPaid || 0);
        setAmount(nextBalance > 0 ? String(nextBalance.toFixed(2)) : "");
        setMethod("pix");
        setPaidAt(new Date().toISOString().slice(0, 10));
        setNotes("");
        setError("");
    }, [open, installmentId, installmentAmount, installmentPaid]);

    async function handleSubmit(e) {
        e?.preventDefault();
        setError("");
        const n = Number((amount || "0").replace(",", "."));
        if (!Number.isFinite(n) || n <= 0) {
            setError("Informe um valor válido maior que zero.");
            return;
        }
        if (n - balance > 0.01) {
            setError(`Valor excede o saldo da parcela (${formatBRL(balance)}).`);
            return;
        }
        try {
            await onSubmit?.({
                installment_id: installment.id,
                amount: n,
                method,
                paid_at: paidAt || undefined,
                notes: notes || undefined,
            });
        } catch (err) {
            setError(err?.response?.data?.detail || err?.message || "Falha ao registrar pagamento.");
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md" data-testid="register-payment-dialog">
                <DialogHeader>
                    <DialogTitle>Registrar pagamento</DialogTitle>
                    <DialogDescription>
                        {accountLabel} · Parcela {installment?.number} · Saldo{" "}
                        <span className="font-medium">{formatBRL(balance)}</span>
                    </DialogDescription>
                </DialogHeader>

                <form className="space-y-4" onSubmit={handleSubmit}>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="pay-amount">Valor pago</Label>
                            <Input
                                id="pay-amount"
                                type="number"
                                step="0.01"
                                min="0"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder="0,00"
                                data-testid="register-payment-amount-input"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="pay-date">Data do pagamento</Label>
                            <Input
                                id="pay-date"
                                type="date"
                                value={paidAt}
                                onChange={(e) => setPaidAt(e.target.value)}
                                data-testid="register-payment-date-input"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label>Método</Label>
                        <RadioGroup
                            value={method}
                            onValueChange={setMethod}
                            className="grid grid-cols-2 gap-2 sm:grid-cols-3"
                            data-testid="register-payment-method-radio"
                        >
                            {Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => (
                                <label
                                    key={value}
                                    className="flex cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-accent/40 has-[:checked]:border-primary has-[:checked]:bg-primary/5"
                                >
                                    <RadioGroupItem value={value} data-testid={`register-payment-method-${value}`} />
                                    <span>{label}</span>
                                </label>
                            ))}
                        </RadioGroup>
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="pay-notes">Observação (opcional)</Label>
                        <Textarea
                            id="pay-notes"
                            rows={2}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Ex.: pagamento parcial via PIX"
                            data-testid="register-payment-notes-textarea"
                        />
                    </div>

                    {error && (
                        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200">
                            {error}
                        </div>
                    )}

                    <DialogFooter className="pt-1">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => onOpenChange?.(false)}
                            data-testid="register-payment-cancel-button"
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            disabled={submitting}
                            data-testid="register-payment-confirm-button"
                        >
                            {submitting ? "Registrando..." : "Confirmar pagamento"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
